import { useTranslation } from 'react-i18next';
import { formatDualDate, formatShortDate } from '@dairy/domain';
import { formatBirr, formatBirrAmount, formatLiters, formatPhone } from '@dairy/shared';
import { useSession } from '@/auth/useSession';

/** G-2, G-3, PL-12 formatting in the user's language and primary calendar. */
export function useFormat() {
  const { t } = useTranslation();
  const profile = useSession().profile;
  const locale = profile?.locale ?? 'am';
  const cal = profile?.primaryCalendar ?? 'EC';
  return {
    locale,
    cal,
    /** "ጥቅምት 2, 2019 (12 Oct 2026)" */
    date: (iso: string | null | undefined) => (iso ? formatDualDate(iso, locale, cal) : '—'),
    /** "4 Nov" / "ጥቅምት 25" */
    short: (iso: string | null | undefined) => (iso ? formatShortDate(iso, locale, cal) : '—'),
    birr: (santim: number | null | undefined) => (santim === null || santim === undefined ? '—' : formatBirr(santim, locale)),
    amount: (santim: number) => formatBirrAmount(santim),
    liters: (ml: number | null | undefined) => (ml === null || ml === undefined ? '—' : `${formatLiters(ml)} ${t('units.l')}`),
    pct: (x: number | null | undefined) => (x === null || x === undefined ? '—' : `${Math.round(x * 1000) / 10}%`),
    phone: formatPhone,
    time: (iso: string | null | undefined) =>
      iso ? new Date(iso).toLocaleTimeString(locale === 'am' ? 'am-ET' : 'en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, numberingSystem: 'latn' }) : '—',
  };
}

/** Age as "2 y 3 m" / "5 m" / "12 d". */
export function useAge() {
  const { t } = useTranslation();
  return (birthDate: string, today: string) => {
    const [by, bm, bd] = birthDate.split('-').map(Number) as [number, number, number];
    const [ty, tm, td] = today.split('-').map(Number) as [number, number, number];
    let months = (ty - by) * 12 + (tm - bm) - (td < bd ? 1 : 0);
    if (months < 1) {
      const days = Math.max(0, Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(by, bm - 1, bd)) / 86_400_000));
      return t('animals.age.days', { count: days });
    }
    const years = Math.floor(months / 12);
    months -= years * 12;
    return years ? t('animals.age.yearsMonths', { years, months }) : t('animals.age.months', { count: months });
  };
}
